import React, { useState, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import { useRouter } from 'next/router';
import { Colors } from 'planoplan-ui-kit/core/colors';
import { Grid } from 'planoplan-ui-kit/core/helpers/grid';
import { handleClickDownload, downloadPlanoplanContextHandler, downloadPlanoplanAuxHandler } from '@modules/helper';
import { textSize, common, Points, marginLevel } from '@styles';
import {
  TABLET_TEXT_WIDTH,
  LAPTOP_TEXT_WIDTH,
  FULLHD_TEXT_WIDTH,
  LAPTOP_BODY_WIDTH,
  FULLHD_BODY_WIDTH,
  DULL_GREY,
} from '@constants/params';
import { NewTitle } from '@components/Title';
import { ArticleCount } from '@components/Article-Count';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { replaceCodeBlocks, replaceContentFromVariable } from '@libs';
import { useTranslations } from '@libs/translations';

export const Content = ({ content }) => {
  const { title, description, date, categoryName, countViews, isOldPage } = content;
  const translations = useTranslations();
  const {
    query: { category },
  } = useRouter();

  const mainPageAnchor = translations.t('site.mainPageAnchor'); // Анкор на главную страницу
  const [key, setKey] = useState('before'); // Ключ нужен что бы заново отрисовать текст и убрать навешанные события на ссылки скачивания
  const bodyRef = useRef();

  const addEventListenerDownloadButtons = () => {
    if (!bodyRef.current) return;

    const buttons = bodyRef.current.querySelectorAll('a[href$=".exe"], a[href$=".pkg"], a[href$=".msi"]');

    buttons.forEach((button) => {
      button.addEventListener('click', (event) => handleClickDownload(event));
      button.addEventListener('contextmenu', (event) => downloadPlanoplanContextHandler(event));
      button.addEventListener('auxclick', (event) => downloadPlanoplanAuxHandler(event));
    });
  };

  const addEventListenerCopyButtons = () => {
    if (!bodyRef.current) return;

    const buttons = bodyRef.current.querySelectorAll('.code-box .copy-btn');

    buttons.forEach((button) => {
      const label = button.textContent;
      let timer = null;

      button.addEventListener('click', () => {
        const code = button.closest('.code-box').querySelector('code');

        if (!code || !navigator.clipboard) return;

        navigator.clipboard
          .writeText(code.textContent)
          .then(() => {
            button.textContent = button.dataset.copiedLabel || label;
            clearTimeout(timer);
            timer = setTimeout(() => {
              button.textContent = label;
            }, 2000);
          })
          .catch(() => {});
      });
    });
  };

  useEffect(() => {
    setKey('after');
    setTimeout(() => {
      addEventListenerDownloadButtons();
      addEventListenerCopyButtons();
    }, 0);
  }, [content.id]);

  const updateData = replaceCodeBlocks(replaceContentFromVariable(content.body, translations.t));

  return (
    <Container>
      <Main>
        <Header>
          <Breadcrumbs
            links={[
              { href: `/`, name: mainPageAnchor },
              { href: `/blog/`, name: translations.t('site.blog.blog') },
              { href: `/blog/${category}/`, name: categoryName },
            ]}
          />
          <NewTitle align="left" marginLevel={'accent_outer'} marginDirection={'top'}>
            {title}
          </NewTitle>
          <Description>{description}</Description>
          <ArticleCount date={date} views={countViews} />
        </Header>

        <Body isOldPage={isOldPage}>
          <div key={key} ref={bodyRef} dangerouslySetInnerHTML={{ __html: updateData }} />
        </Body>
      </Main>
    </Container>
  );
};

const Container = styled.section`
  ${marginLevel('bottom', 'frog_green')};
  ${Grid.row};
  max-width: 1440px;
  margin-left: auto;
  margin-right: auto;
  justify-content: center;
`;

const Main = styled.article`
  ${Grid.post(2)};
  ${Grid.push(2)};
  ${Grid.column(32, 36)};

  @media ${Points.laptop} {
    width: ${LAPTOP_BODY_WIDTH};
    margin-left: auto;
    margin-right: auto;
  }

  @media ${Points.fullhd} {
    width: ${FULLHD_BODY_WIDTH};
  }
`;

const Header = styled.div`
  @media ${Points.tablet_portrait} {
    width: ${TABLET_TEXT_WIDTH};
    margin-left: auto;
    margin-right: auto;
  }

  @media ${Points.laptop} {
    width: ${LAPTOP_TEXT_WIDTH};
  }

  @media ${Points.fullhd} {
    width: ${FULLHD_TEXT_WIDTH};
  }
`;

const Description = styled.p`
  color: ${DULL_GREY};
  ${textSize('subtitle')};
  ${marginLevel('bottom', 'blog_description_bottom')};
  ${marginLevel('top', 'blog_description_top')};

  @media ${Points.tablet_portrait} {
    ${Grid.suffix(0)};
  }

  @media ${Points.laptop} {
    ${Grid.suffix(0)};
  }
`;

const Body = styled.div`
  ${marginLevel('top', 'blog_body')};

  @media ${Points.laptop} {
    width: ${LAPTOP_BODY_WIDTH};
    margin-left: auto;
    margin-right: auto;
  }

  @media ${Points.fullhd} {
    width: ${FULLHD_BODY_WIDTH};
  }

  ${(p) =>
    p.isOldPage &&
    css`
      span,
      a,
      b,
      i,
      li,
      font {
        font-family: 'CoFoSans', sans-serif !important;
        font-size: 14px !important;
        line-height: 22px;
        font-weight: 300;

        @media ${Points.tablet_portrait} {
          font-size: 18px !important;
          line-height: 28px;
        }

        @media ${Points.laptop} {
          font-size: 22px !important;
          line-height: 36px;
        }
      }

      p {
        padding: 0 !important;
      }
    `}

  .video {
    position: relative;
    display: flex;
    padding-top: 56.25%;

    iframe {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
    }
  }

  .virtualTour {
    height: 85vh;

    @media (min-width: 1280px) {
      height: auto;
      aspect-ratio: 1.778;
    }

    iframe {
      width: 100%;
    }
  }

  figure {
    ${marginLevel('top', 'blog_img')};
    ${marginLevel('bottom', 'accent_outer')};
    margin-left: auto;
    margin-right: auto;
  }

  figure:not(.video):not(.column):not(.mount) {
    @media ${Points.laptop} {
      width: ${LAPTOP_BODY_WIDTH};
    }

    @media ${Points.fullhd} {
      width: ${FULLHD_BODY_WIDTH};
    }
  }

  figcaption {
    margin-top: 5px;
    font-size: 12px;
    line-height: 1.115;
    color: ${Colors.main_gray};
    text-align: right;

    @media ${Points.fullhd} {
      font-size: 14px;
    }
  }

  img {
    width: 100%;
    display: block;
  }

  .column,
  .column img {
    width: 100%;

    @media ${Points.tablet_portrait} {
      width: ${TABLET_TEXT_WIDTH};
    }

    @media ${Points.laptop} {
      width: ${LAPTOP_TEXT_WIDTH};
    }

    @media ${Points.fullhd} {
      width: ${FULLHD_TEXT_WIDTH};
    }
  }

  .border img {
    border: 1px solid #e5e5e5;
  }

  .image-pair-wrapper {
    display: flex;
    flex-direction: column;

    @media ${Points.tablet_portrait} {
      width: ${TABLET_TEXT_WIDTH};
      margin-right: auto;
      margin-left: auto;
    }

    @media ${Points.tablet_landscape} {
      flex-direction: row;
      align-items: stretch;
      gap: 0 20px;
      width: 100%;
    }
  }

  .image-pair-wrapper:not(:has(figure)) span:not(:last-child) {
    margin-bottom: 32px;

    @media ${Points.tablet_portrait} {
      margin-bottom: 35px;
    }

    @media ${Points.tablet_landscape} {
      margin-bottom: 0;
    }
  }

  .image-pair-wrapper img {
    @media ${Points.laptop} {
      max-width: calc(${LAPTOP_BODY_WIDTH} / 2 - 10px);
      min-width: calc(${LAPTOP_BODY_WIDTH} / 2 - 10px);
    }

    @media ${Points.fullhd} {
      max-width: calc(${FULLHD_BODY_WIDTH} / 2 - 10px);
      min-width: calc(${FULLHD_BODY_WIDTH} / 2 - 10px);
    }
  }

  .image-pair-wrapper + figcaption {
    @media ${Points.tablet_portrait} {
      width: 600px;
      margin-left: auto;
      margin-right: auto;
    }

    @media ${Points.tablet_landscape} {
      width: 100%;
    }
  }

  div.image-pair-wrapper figcaption {
    @media ${Points.tablet_landscape} {
      text-align: center;
    }
  }

  .mount {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: 24px;
    background-color: ${Colors.main_gray};

    @media ${Points.tablet_portrait} {
      width: ${TABLET_TEXT_WIDTH};
      padding-top: 25px;
    }

    @media ${Points.laptop} {
      width: ${LAPTOP_TEXT_WIDTH};
      padding-top: 30px;
    }

    @media ${Points.fullhd} {
      width: ${FULLHD_TEXT_WIDTH};
    }
  }

  .mount img {
    width: 90%;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: 28px;

    @media ${Points.tablet_portrait} {
      width: fit-content;
      max-width: 90%;
      margin-bottom: 30px;
    }

    @media ${Points.laptop} {
      margin-bottom: 35px;
    }
  }

  .mount figcaption {
    background-color: ${Colors.white};
    width: 100%;
    padding-top: 5px;
  }

  h1 {
    color: ${Colors.main_black};
    margin-bottom: 10px;

    @media ${Points.tablet_portrait} {
      width: ${TABLET_TEXT_WIDTH};
    }

    @media ${Points.laptop} {
      width: ${LAPTOP_TEXT_WIDTH};
      margin-bottom: 15px;
    }

    @media ${Points.fullhd} {
      width: ${FULLHD_TEXT_WIDTH};
      margin-bottom: 20px;
    }
  }

  h2,
  h3,
  h4,
  h5,
  h6 {
    color: ${Colors.main_dark};
    font-weight: 400;
    font-weight: 400;

    @media ${Points.tablet_portrait} {
      width: ${TABLET_TEXT_WIDTH};
      margin-left: auto;
      margin-right: auto;
    }

    @media ${Points.laptop} {
      width: ${LAPTOP_TEXT_WIDTH};
    }

    @media ${Points.fullhd} {
      width: ${FULLHD_TEXT_WIDTH};
    }
  }

  h2 {
    ${textSize('large_card_header')};
    ${marginLevel('bottom', 'accent_outer')};
    ${marginLevel('top', 'blog_header')};
  }

  .interview-question {
    ${textSize('subtitle')};
    font-weight: 400;
    color: ${Colors.main_dark};
    ${marginLevel('bottom', 'interview')};
    ${marginLevel('top', 'accent_outer')};
  }

  p:not(.bluebox p):not(.ctabox p):not(.list__content p):not(.visual-heading):not(.quote-text p) {
    color: ${Colors.main_gray};
    margin-bottom: 20px;
    ${textSize('article')};
    font-weight: 300;

    @media ${Points.tablet_portrait} {
      width: ${TABLET_TEXT_WIDTH};
      margin-left: auto;
      margin-right: auto;
    }

    @media ${Points.laptop} {
      width: ${LAPTOP_TEXT_WIDTH};
    }

    @media ${Points.fullhd} {
      width: ${FULLHD_TEXT_WIDTH};
    }

    &:last-child {
      ${marginLevel('bottom', 'accent_outer')};
    }
  }

  .visual-heading {
    margin-bottom: 10px;
    font-weight: 300;
    font-size: 20px;
    line-height: 1.2;
    ${marginLevel('top', 'accent_outer')};

    @media ${Points.tablet_portrait} {
      width: ${TABLET_TEXT_WIDTH};
      font-size: 22px;
      margin-left: auto;
      margin-right: auto;
    }

    @media ${Points.laptop} {
      width: ${LAPTOP_TEXT_WIDTH};
      font-size: 24px;
      line-height: 1.1;
    }

    @media ${Points.fullhd} {
      width: ${FULLHD_TEXT_WIDTH};
      font-size: 30px;
      line-height: 1.115;
    }
  }

  ul {
    ${common('listBlog')};
  }

  ol,
  ul {
    color: ${Colors.main_gray};
    ${marginLevel('bottom', 'accent_outer')};
    padding-left: 20px;

    @media ${Points.tablet_portrait} {
      max-width: ${TABLET_TEXT_WIDTH};
      margin-left: auto;
      margin-right: auto;
    }

    @media ${Points.laptop} {
      max-width: ${LAPTOP_TEXT_WIDTH};
    }

    @media ${Points.fullhd} {
      max-width: ${FULLHD_TEXT_WIDTH};
    }
  }

  .list__content {
    @media ${Points.tablet_portrait} {
      width: calc(100% - 58px);
    }
  }

  .list__content .visual-heading {
    display: block;
    font-size: 18px;
    line-height: 1.2;
    font-weight: 400;
    color: ${Colors.main_dark};
    margin-bottom: 10px;
    margin-top: 0;

    @media ${Points.tablet_portrait} {
      font-size: 20px;
    }

    @media ${Points.fullhd} {
      font-size: 24px;
    }
  }

  .bluebox .visual-heading,
  .ctabox .visual-heading {
    margin-top: 0;
    padding-top: 0;
  }

  .list__content ul {
    margin-bottom: 0;
  }

  .list__content li strong {
    ${textSize('article')}
    display: inline;
  }

  li {
    ${textSize('article')};
    margin-bottom: 10px;
    font-weight: 300;

    @media ${Points.laptop} {
      margin-bottom: 14px;
    }
  }

  a:not(.bluebox-button):not(.cta-button):not(.cta-button-text) {
    ${common('a')};
    color: ${Colors.second_link};
  }

  .list {
    display: flex;
    justify-content: start;
    ${marginLevel('both', 'whisper')};

    p {
      padding: 0;
    }
  }

  .list + :not(.list) {
    ${marginLevel('top', 'accent_outer')};
  }

  b,
  strong:not(.list__content > strong):not(.bluebox > strong):not(.ctabox > strong) {
    font-weight: 500;
  }

  br {
    height: 24px;

    @media ${Points.tablet_portrait} {
      height: 25px;
    }

    @media ${Points.laptop} {
      height: 20px;
    }

    @media ${Points.fullhd} {
      height: 30px;
    }
  }

  br + br {
    height: 24px;

    @media ${Points.tablet_portrait} {
      height: 20px;
    }

    @media ${Points.laptop} {
      height: 20px;
    }

    @media ${Points.fullhd} {
      height: 20px;
    }
  }

  code {
    font-family: 'CoFoSans Mono', monospace;
    font-weight: 400;
    letter-spacing: 0%;
    font-variant-numeric-fraction: stacked-fractions;
    background-color: ${Colors.beige_100};
    padding: 6px;
    border-radius: 4px;
    line-height: 1.4;
  }

  .code-box {
    position: relative;
    margin-bottom: 30px;
    background-color: ${Colors.main_dark};
    color: #f8f8f2;
    padding: 15px 15px 5px;
    border-radius: 8px;
    font-family: 'Courier New', Courier, monospace;

    @media ${Points.tablet_portrait} {
      width: 600px;
      margin: 20px auto 30px;
    }

    @media ${Points.laptop} {
      width: 640px;
    }

    @media ${Points.fullhd} {
      width: 690px;
    }
  }

  .copy-btn {
    position: absolute;
    top: 10px;
    right: 10px;
    background-color: ${Colors.blue_500};
    color: white;
    border: none;
    padding: 6px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    opacity: 0;
    transition: opacity 0.2s, background-color 0.2s;
  }

  .code-box:hover .copy-btn,
  .copy-btn:focus-visible {
    opacity: 1;
  }

  @media (hover: none) {
    .copy-btn {
      opacity: 1;
    }
  }

  .copy-btn:hover {
    background-color: ${Colors.blue_400};
  }

  pre {
    margin: 0;
    overflow-x: auto;
  }

  .code-box pre {
    padding-bottom: 10px;

    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.3) transparent;
  }

  .code-box pre::-webkit-scrollbar {
    height: 5px;
    background: transparent;
  }

  .code-box pre::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.3);
    border-radius: 3px;
  }

  pre code {
    color: #f8f8f2;
    background-color: transparent;
    padding: 0;
  }

  .callout {
    margin: 20px 0;
    padding: 15px;
    padding-left: 46px;
    background-color: #f2e9e2;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512' fill='%233F3F3F'%3e%3cpath d='M256 48a208 208 0 1 1 0 416 208 208 0 1 1 0-416zm0 464a256 256 0 1 0 0-512 256 256 0 1 0 0 512zM216 336c-13.3 0-24 10.7-24 24s10.7 24 24 24l80 0c13.3 0 24-10.7 24-24s-10.7-24-24-24l-8 0 0-88c0-13.3-10.7-24-24-24l-48 0c-13.3 0-24 10.7-24 24s10.7 24 24 24l24 0 0 64-24 0zm40-144a32 32 0 1 0 0-64 32 32 0 1 0 0 64z'/%3e%3c/svg%3e");
    background-size: 16px 16px;
    background-position: 15px 17px;
    background-repeat: no-repeat;

    @media ${Points.tablet_portrait} {
      width: 600px;
      margin: 20px auto;
    }

    @media ${Points.laptop} {
      width: 640px;
    }

    @media ${Points.fullhd} {
      width: 690px;
    }
  }

  .callout.warning {
    background-color: #f2e9e2;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512' fill='%232c35ef'%3e%3cpath d='M256 512a256 256 0 1 1 0-512 256 256 0 1 1 0 512zm0-464a208 208 0 1 0 0 416 208 208 0 1 0 0-416zm0 336a32 32 0 1 1 0-64 32 32 0 1 1 0 64zm0-256c18.2 0 32.7 15.5 31.4 33.7l-7.4 104c-.9 12.6-11.4 22.3-23.9 22.3-12.6 0-23-9.7-23.9-22.3l-7.4-104C223.3 143.5 237.8 128 256 128z'/%3e%3c/svg%3e");
  }

  .callout.danger {
    background-color: #f2e9e2;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512' fill='%23e76422'%3e%3cpath d='M256.5 0c14.7 0 28.2 8.1 35.2 21l216 400c6.7 12.4 6.4 27.4-.8 39.5S486.6 480 472.5 480l-432 0c-14.1 0-27.1-7.4-34.3-19.5s-7.5-27.1-.8-39.5l216-400c7-12.9 20.5-21 35.2-21zM53.9 432L459.1 432 256.5 56.8 53.9 432zm202.6-40a32 32 0 1 1 0-64 32 32 0 1 1 0 64zm0-208c18.6 0 33 16.1 31 34.6l-7.1 64.1C279 294.8 268.7 304 256.5 304s-22.5-9.2-23.8-21.3l-7.1-64.1c-2-18.5 12.4-34.6 31-34.6z'/%3e%3c/svg%3e");
  }

  table {
    border-collapse: collapse;
    width: 100%;
    margin: 20px 0;

    display: block;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;

    @media ${Points.tablet_portrait} {
      display: table;
      overflow-x: visible;
    }
  }

  th,
  td {
    max-width: calc(100vw - 16px);

    @media ${Points.tablet_portrait} {
      max-width: none;
    }
  }

  th {
    padding: 10px 8px;
    border: 1px solid #ddd;
    font-weight: 300;
    text-align: left;
  }

  thead th {
    text-align: center;
    font-weight: 500;
    background-color: #eaeaea;
  }
`;
